import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { Check, Minus } from "lucide-react";

import { Column, Pagination, Table, Td } from "@/components/admin/table";
import { ReportPipelinePanel } from "@/components/admin/report-pipeline";
import { usePaged } from "@/components/admin/use-paged";
import { SortHeader, sortRows } from "@/components/admin/sort-header";
import {
  DEFAULT_RANGE,
  RangeControl,
  rangeParams,
  type Range,
} from "@/components/admin/range-control";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { API_URL, errorDetail, fetchJson } from "@/lib/admin-api";
import { cn } from "@/lib/utils";

/**
 * Customer health (POS2-1138 CP4).
 *
 * One row per site across **every** organisation, worst first, with the reason under the pill so
 * nobody has to reverse-engineer a row. Churn shows up in engagement months before it shows up in a
 * renewal call; this is the screen that says so in August rather than November.
 *
 * Three things this file deliberately does *not* do:
 *
 * * **It never computes the health score.** `health_score`, `health_level` and `health_reasons`
 *   come from SQL (decision 17), so the sort key, the pill and the reason line cannot disagree —
 *   and so the pill cannot start drifting with the date range, which is exactly what deriving it
 *   from the range-scoped fields would cause.
 * * **It never sends organisation context.** The endpoint ignores `X-Org-Id` (decision 24); the
 *   header here says so in words, because an admin with a sticky selection seeing nine sites and
 *   believing that is the estate is a failure with no symptom.
 * * **It never claims a device is offline.** A device with no captures for three days is *quiet*,
 *   with the number of days — a closed kitchen and a dead camera are indistinguishable from here.
 *
 * **English only** — the `/admin/*` carve-out from Phase 6.4.
 */

const BASE = `${API_URL}/api/v1/admin`;

type HealthSite = {
  site_id: string;
  site_name: string;
  org_name: string;
  site_timezone: string;
  correct_pct: number | null;
  member_user_ids: string[];
  active_user_ids: string[];
  last_login_at_in_range: string | null;
  last_report_sent_at: string | null;
  last_report_probably_read: boolean | null;
  last_report_in_range: boolean | null;
  current_last_login_at: string | null;
  current_correct_pct: number | null;
  devices_total: number;
  devices_reporting: number;
  devices_quiet: number;
  devices_never: number;
  current_reports_unread: boolean;
  current_last_report_sent_at: string | null;
  health_score: number;
  health_level: number;
  health_reasons: string[];
  login_gap_days: number | null;
  device_quiet_days: number | null;
  /** One entry per quiet device, oldest first. Null when none are quiet (POS2-1281). */
  quiet_devices: { serial: string; last_capture_at: string }[] | null;
};

type HealthResponse = {
  period_from: string;
  period_to: string;
  login_history_from: string | null;
  emails: Record<string, string>;
  sites: HealthSite[];
};

type SortKey = "site" | "correct" | "login" | "report" | "devices" | "health";

/**
 * Six of the eight are sortable, and two of those six sort on keys that are not columns at all —
 * devices as a reporting ratio, health as the score SQL returned. That is why the endpoint returns
 * every site unpaginated and the sort lives in the browser (decision 27).
 *
 * "Users" and "Report read" are not sortable: sorting people by their initials sorts nothing, and a
 * three-state inference is a filter rather than an order.
 */
const COLUMNS: { column: Column; sort?: SortKey }[] = [
  { column: { label: "Site" }, sort: "site" },
  { column: { label: "Users" } },
  { column: { label: "Correct", numeric: true }, sort: "correct" },
  { column: { label: "Last login" }, sort: "login" },
  { column: { label: "Last report sent" }, sort: "report" },
  { column: { label: "Report read" } },
  { column: { label: "Devices reporting", numeric: true }, sort: "devices" },
  { column: { label: "Account health" }, sort: "health" },
];

/** The mock's bands, restated here only as colours — the RPC owns the 45% floor that scores. */
const CORRECT_GREEN = 60;
const CORRECT_AMBER = 45;

const PILLS = [
  { label: "Healthy", className: "bg-emerald-50 text-emerald-700" },
  { label: "At risk", className: "bg-amber-50 text-amber-700" },
  { label: "Churn risk", className: "bg-red-50 text-red-700" },
];

export default function AdminCustomerHealthPage() {
  const [range, setRange] = useState<Range>(DEFAULT_RANGE);
  const [sortKey, setSortKey] = useState<SortKey>("health");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const headers = useApiHeaders();
  const navigate = useNavigate();

  const query = useMemo(() => rangeParams(range), [range]);
  const health = useQuery<HealthResponse>({
    queryKey: ["admin-customer-health", query],
    queryFn: () =>
      fetchJson<HealthResponse>(`${BASE}/customer-health?${new URLSearchParams(query)}`, headers),
  });

  const sites = useMemo(() => health.data?.sites ?? [], [health.data]);
  const sorted = useMemo(
    () =>
      sortRows<HealthSite, SortKey>(sites, sortKey, direction, {
        site: (row) => row.site_name.toLowerCase(),
        correct: (row) => row.correct_pct,
        login: (row) => row.last_login_at_in_range,
        report: (row) => row.last_report_sent_at ?? row.current_last_report_sent_at,
        // Two of the six sort keys are derived rather than columns, which is the whole reason the
        // endpoint returns every site unpaginated and the sort lives here. The tiebreak on total
        // keeps a 1/1 site above a 4/4 one rather than tying them.
        devices: (row) =>
          row.devices_total === 0
            ? null
            : row.devices_reporting / row.devices_total + row.devices_total * 1e-4,
        health: (row) => row.health_score,
      }),
    [sites, sortKey, direction],
  );
  const paged = usePaged(sorted);

  const columns: Column[] = COLUMNS.map(({ column, sort }) =>
    sort
      ? {
          ...column,
          ariaSort:
            sortKey === sort
              ? direction === "asc"
                ? "ascending"
                : "descending"
              : ("none" as const),
          header: (
            <SortHeader
              label={column.label}
              sortKey={sort}
              active={sortKey}
              direction={direction}
              column={column}
              onSort={toggleSort}
            />
          ),
        }
      : column,
  );

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setDirection(direction === "asc" ? "desc" : "asc");
      return;
    }
    setSortKey(key);
    // A new column opens on the end that is worth looking at: worst-first for the health score,
    // alphabetical for a name, and oldest-first for a date, because a stale stamp is the finding.
    setDirection(key === "site" ? "asc" : key === "health" ? "desc" : "asc");
  }

  const churnRisk = sites.filter((site) => site.health_level === 2).length;
  const atRisk = sites.filter((site) => site.health_level === 1).length;
  const historyGap =
    health.data &&
    (!health.data.login_history_from ||
      health.data.period_from < health.data.login_history_from.slice(0, 10));

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-xl font-extrabold tracking-tight text-gray-900">
            Customer health
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-testid="health-summary">
            {/* All sites, all organisations — stated, because the one failure mode of this screen
                that has no symptom is a platform admin with a sticky org selection believing nine
                sites are the estate. */}
            All sites · all organisations · <b className="text-gray-700">{sites.length} sites</b> ·{" "}
            <b className="text-red-600">{churnRisk} churn risk</b> ·{" "}
            <b className="text-amber-600">{atRisk} at risk</b>
          </p>
        </div>
        <RangeControl
          range={range}
          onChange={setRange}
          resolved={
            health.data ? { from: health.data.period_from, to: health.data.period_to } : undefined
          }
        />
      </div>

      {/* Above the table and outside the range, because it answers a different question from every
          column below it: not "how is this customer doing" but "is the pipeline running at all"
          (POS2-1298). Renders nothing when there is nothing to say. */}
      <ReportPipelinePanel />

      {/* "No sites." on a failed request would be the worst empty state on this screen: the whole
          point of the range validation is that an empty row set must never stand in for an error. */}
      {health.isError ? (
        <p
          role="alert"
          data-testid="health-error"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {errorDetail(health.error)}
        </p>
      ) : (
        <Table<HealthSite>
          columns={columns}
          rows={paged.page}
          isLoading={health.isLoading}
          empty="No sites."
          renderRow={(site, columns) => (
            <tr
              key={site.site_id}
              role="link"
              tabIndex={0}
              data-testid={`health-row-${site.site_id}`}
              className="cursor-pointer border-b border-black/[0.06] last:border-b-0 hover:bg-gray-50"
              onClick={() => openLog(navigate, site, health.data!)}
              onKeyDown={(event) => {
                if (event.key === "Enter") openLog(navigate, site, health.data!);
              }}
            >
              <Td column={columns[0]}>
                <span className="block font-medium text-gray-900">{site.site_name}</span>
                {/* "Operator" is the organisation, not the account — the same value the labelling
                  admin screen shows, so the two cannot disagree about who runs a site. */}
                <span className="block text-xs text-gray-400">{site.org_name}</span>
              </Td>
              <Td column={columns[1]}>
                <Bubbles site={site} emails={health.data?.emails ?? {}} />
              </Td>
              <Td column={columns[2]}>
                <Correct value={site.correct_pct} />
              </Td>
              <Td column={columns[3]}>
                <Stamp
                  at={site.last_login_at_in_range}
                  current={site.current_last_login_at}
                  inRange={site.last_login_at_in_range !== null}
                  rangeEnd={health.data!.period_to}
                  emptyNote="never logged in"
                  amberAfter={14}
                  redAfter={30}
                />
              </Td>
              <Td column={columns[4]}>
                {/* 35 days, not the 30 the login stamp uses and not the "red past 30" the ticket
                  states as a general rule. Deliberate, and the mock agrees: the longest cadence is
                  Monthly, so a send on day 31 of a 31-day month is a report that arrived exactly on
                  time. Red at 30 would put a colour that means "pick up the phone" on the most
                  punctual schedule the product has, every month. 35 clears the longest month and
                  still catches a schedule that has actually stopped. */}
                <Stamp
                  at={site.last_report_sent_at}
                  current={site.current_last_report_sent_at}
                  inRange={site.last_report_in_range === true}
                  rangeEnd={health.data!.period_to}
                  emptyNote="none sent yet"
                  amberAfter={14}
                  redAfter={35}
                />
              </Td>
              <Td column={columns[5]}>
                <ProbablyRead value={site.last_report_probably_read} />
              </Td>
              <Td column={columns[6]}>
                <Devices site={site} />
              </Td>
              <Td column={columns[7]}>
                <span
                  className={cn(
                    "inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
                    PILLS[site.health_level].className,
                  )}
                  data-testid={`health-pill-${site.site_id}`}
                >
                  {PILLS[site.health_level].label}
                </span>
                <span className="mt-1 block text-xs text-gray-400">{reasonLine(site)}</span>
              </Td>
            </tr>
          )}
          footer={paged.bar ? <Pagination {...paged.bar} /> : undefined}
        />
      )}

      {historyGap && (
        <p className="mt-3 text-xs text-gray-500" data-testid="login-history-note">
          {health.data?.login_history_from
            ? `Login history starts ${format(parseISO(health.data.login_history_from), "dd/MM/yy")}. Earlier ranges show only each user's most recent sign-in before that date.`
            : "Login history has not started yet. Ranges before this release show only each user's most recent sign-in."}
        </p>
      )}
    </div>
  );
}

function openLog(navigate: ReturnType<typeof useNavigate>, site: HealthSite, data: HealthResponse) {
  // The whole question travels, not just the site: the log opens on the same window the row was
  // computed over, which is what makes "click through to what actually happened" mean anything.
  navigate(`/admin/activity?site=${site.site_id}&from=${data.period_from}&to=${data.period_to}`);
}

function Bubbles({ site, emails }: { site: HealthSite; emails: Record<string, string> }) {
  const shown = site.member_user_ids.slice(0, 4);
  const extra = site.member_user_ids.length - shown.length;
  const active = new Set(site.active_user_ids);
  return (
    <span className="block">
      <span className="flex items-center">
        {shown.map((id) => (
          <span
            key={id}
            title={emails[id] ?? id}
            className={cn(
              "-ml-1.5 grid size-6 place-items-center rounded-full border-2 border-white bg-gray-500 text-[10px] font-semibold text-white first:ml-0",
              // Faded, not hidden: the point of the row is how many of the people who *can* see
              // this site actually did.
              !active.has(id) && "opacity-30",
            )}
          >
            {initials(emails[id] ?? id)}
          </span>
        ))}
        {extra > 0 && (
          <span className="-ml-1.5 grid size-6 place-items-center rounded-full border-2 border-white bg-gray-100 text-[10px] font-semibold text-gray-500">
            +{extra}
          </span>
        )}
      </span>
      <span
        className={cn(
          "mt-1 block text-xs",
          site.active_user_ids.length === 0 ? "text-red-600" : "text-gray-400",
        )}
      >
        {site.active_user_ids.length} of {site.member_user_ids.length} active
      </span>
    </span>
  );
}

function Correct({ value }: { value: number | null }) {
  // A dash, never a zero. A site with no labelling in the window has no correct rate, and "0.0%"
  // would read as "nothing was correct" — which is also why the score never treats it as low.
  if (value === null) return <span className="text-gray-300">—</span>;
  return (
    <span
      className={cn(
        "font-semibold tabular-nums",
        value >= CORRECT_GREEN
          ? "text-emerald-700"
          : value >= CORRECT_AMBER
            ? "text-amber-700"
            : "text-red-600",
      )}
    >
      {value.toFixed(1)}%
    </span>
  );
}

/**
 * A date, its staleness colour, and what it means.
 *
 * The colour follows **absolute** staleness rather than the selected range — amber past 14 days,
 * red past 30 — because a date that goes green when you widen the window is a date nobody can act
 * on. Red means pick up the phone.
 */
function Stamp({
  at,
  current,
  inRange,
  rangeEnd,
  emptyNote,
  amberAfter,
  redAfter,
}: {
  at: string | null;
  current: string | null;
  inRange: boolean;
  rangeEnd: string;
  emptyNote: string;
  amberAfter: number;
  redAfter: number;
}) {
  // `current` first, not `at` (POS2-1260). Both are `max()` aggregates and `current` is the
  // unbounded one, so it is always the more recent whenever it is set — which makes this the true
  // latest value rather than the latest one that happens to land inside the window. `at ?? current`
  // only fell back when there was nothing in the range at all, so an older in-range value hid a
  // newer one: a report sent today read as yesterday's send, and an actively-used site's login
  // stamp understated by a day. `at` stays as the fallback and as what `inRange` is derived from.
  const shown = current ?? at;
  if (!shown) {
    return (
      <span className="block">
        <span className="block font-semibold text-gray-700">—</span>
        <span className="block text-xs text-gray-400">{emptyNote}</span>
      </span>
    );
  }
  const ago = differenceInCalendarDays(new Date(), parseISO(shown));
  return (
    <span className="block">
      <span
        className={cn(
          "block font-semibold tabular-nums",
          ago > redAfter ? "text-red-600" : ago > amberAfter ? "text-amber-700" : "text-gray-700",
        )}
      >
        {format(parseISO(shown), "dd/MM/yy")}
      </span>
      <span className="block text-xs text-gray-400">
        {/* Three facts, three strings, none of them an empty cell: inside the window, older than
            it, newer than it. The first two were already here — "none in this range" exists
            because an empty cell "would read as they were never sent one". The third is
            POS2-1254: every range ends yesterday, so anything from today landed on the wrong side
            of that same distinction and read as never. Tested in that order, because in-range
            wins and that is what leaves every other row rendering exactly as it did. */}
        {inRange
          ? ago === 0
            ? "today"
            : ago === 1
              ? "yesterday"
              : `${ago} days ago`
          : format(parseISO(shown), "yyyy-MM-dd") > rangeEnd
            ? "after this range"
            : "none in this range"}
      </span>
    </span>
  );
}

function ProbablyRead({ value }: { value: boolean | null }) {
  // "Probably", never "read". `email.opened` never fires — the Resend webhook is unconfigured — so
  // this is inferred from how long after the send the download link was clicked. A scanner fetches
  // within seconds and never returns; a person reads later. Saying "probably" is the difference
  // between a number and a claim.
  if (value === null) return <span className="text-gray-300">—</span>;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 font-semibold",
        value ? "text-emerald-700" : "text-red-600",
      )}
      title="Inferred from the gap between the send and the download click, not measured"
    >
      {value ? <Check className="size-3.5" /> : <Minus className="size-3.5" />}
      {value ? "Probably" : "No sign"}
    </span>
  );
}

function Devices({ site }: { site: HealthSite }) {
  if (site.devices_total === 0) {
    return (
      <span className="block">
        <span className="text-gray-300">—</span>
        {site.devices_never > 0 && (
          <span className="mt-1 block text-xs text-gray-400">
            {site.devices_never} never reported
          </span>
        )}
      </span>
    );
  }
  const quiet = site.devices_quiet > 0;
  return (
    <span className="block">
      <span
        className={cn(
          "inline-flex items-center gap-1.5 font-semibold tabular-nums",
          quiet ? "text-red-600" : "text-gray-700",
        )}
      >
        <span className={cn("size-[7px] rounded-full", quiet ? "bg-red-500" : "bg-emerald-500")} />
        {site.devices_reporting}/{site.devices_total}
      </span>
      {site.devices_never > 0 && (
        <span className="mt-1 block text-xs text-gray-400">
          {site.devices_never} never reported
        </span>
      )}
    </span>
  );
}

/**
 * The reason under the pill, in English, from the codes SQL returned (decision 19).
 *
 * Score and level stay authoritative in SQL so the sort and the pill agree; copy lives where copy
 * belongs. A reason only appears when it actually contributed — `reports_unread` is absent while
 * its threshold is still an estimate, because a line explaining a point the score never added is
 * worse than no line.
 */
/**
 * How long each quiet device has been quiet (POS2-1281).
 *
 * This used to read `${devices_quiet} devices quiet for ${device_quiet_days} days`, pairing a count
 * over the quiet group with the age of the *oldest* member of it. Both numbers were right and they
 * described different devices, so two devices last seen 100 days and 4 days ago read as "2 devices
 * quiet for 100 days" — a claim nobody had made. Only ever wrong above one device, which is why the
 * singular form is left exactly as it was: it has always been accurate.
 *
 * Named devices rather than a vaguer-but-shorter "longest 100 days", because the operator reading
 * this row wants to know which device to go and check.
 *
 * **Capped at two, and the cap is what bounds the row height.** This span has no truncation and no
 * max width, so the length of the string *is* how tall the row gets — and it already carries up to
 * four reasons joined with "·". Nine quiet devices on 16-character serials measured 180 characters
 * against 121 before, i.e. half as long again on the worst line the screen can produce. Two answers
 * "which do I check first", the count ahead of the colon is always the true total, and
 * `quietTitle` puts every one of them in the tooltip so the cap hides nothing.
 */
const NAMED_QUIET = 2;

function quietSentence(site: HealthSite): string {
  const count = `${site.devices_quiet} device${site.devices_quiet === 1 ? "" : "s"} quiet`;

  // **The one-device sentence does not touch the new field at all**, which is deliberate rather
  // than lazy. `device_quiet_days` is floored server-side against `now()` at query time; deriving
  // the same number in the browser from a timestamp can disagree by a day at the boundary, and
  // that is a visible change to the one case that was never wrong. Also covers a null list — no
  // quiet devices, or a response cached before the column existed.
  if (site.devices_quiet <= 1 || !site.quiet_devices?.length) {
    return site.device_quiet_days === null ? count : `${count} for ${site.device_quiet_days} days`;
  }

  // Plural has no server-side per-device figure to use, so the ages are derived here. The first is
  // `device_quiet_days` by construction — the list is ordered oldest first and that column is the
  // MIN over the same group.
  const shown = site.quiet_devices
    .slice(0, NAMED_QUIET)
    .map((d) => `${d.serial} ${daysSince(d.last_capture_at)}d`)
    .join(", ");
  const rest = site.quiet_devices.length - NAMED_QUIET;
  return `${count}: ${shown}${rest > 0 ? `, +${rest} more` : ""}`;
}

/** Every quiet device, for the tooltip — so the cap above hides nothing, it only shortens. */
function quietTitle(site: HealthSite): string | undefined {
  if (!site.quiet_devices?.length) return undefined;
  return site.quiet_devices
    .map((d) => `${d.serial} — quiet ${daysSince(d.last_capture_at)} days`)
    .join("\n");
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

/**
 * The grey line under the pill: every firing reason, joined.
 *
 * Returns nodes rather than a string because the quiet-device reason carries its own tooltip, and
 * a `title` on the wrapping span would belong to all of them — hovering "No login in 69 days" would
 * have listed devices. A tooltip has to belong to the thing it describes (POS2-1281).
 */
function reasonLine(site: HealthSite): ReactNode {
  const parts: ReactNode[] = site.health_reasons.map((code) => {
    switch (code) {
      case "never_logged_in":
        return "Never logged in";
      case "login_gap_30":
      case "login_gap_14":
        return `No login in ${site.login_gap_days} days`;
      case "device_quiet":
        return (
          <span
            key={code}
            title={quietTitle(site)}
            // Dotted underline only when the cap is actually hiding something, so it reads as
            // "there is more here" rather than decorating a line that already says everything.
            className={cn(
              (site.quiet_devices?.length ?? 0) > NAMED_QUIET &&
                "underline decoration-dotted underline-offset-2",
            )}
          >
            {quietSentence(site)}
          </span>
        );
      case "reports_unread":
        return "Recent reports show no sign of being read";
      case "correct_low":
        // Names its window (POS2-1262). The Correct column two cells away prints the *range-scoped*
        // rate while this prints the fixed trailing 14 days the score is built from (decision 18),
        // so on a wide range the two differ by several points — and two numbers labelled "Correct"
        // that disagree with no explanation read as a bug rather than as two questions. The
        // qualifier goes on the number that needs it, matching "1 device quiet for 742 days" beside
        // it rather than adding another sentence to the header.
        return `Correct ${site.current_correct_pct}% over 14 days`;
      default:
        return code;
    }
  });
  if (parts.length === 0) return "No signals firing";
  return parts.map((part, i) => (
    <Fragment key={site.health_reasons[i]}>
      {i > 0 && " · "}
      {part}
    </Fragment>
  ));
}

function initials(email: string): string {
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}
